import { MediaKind } from "@/api/types";

/**
 * The arithmetic behind the crop overlay, with no DOM in it.
 *
 * <p>One rule governs, and the mode decides which: <b>the frame and the image must stay in
 * contact</b>. In {@code FILL} the image covers the frame, so no gap can open. In {@code FIT} the
 * image sits wholly inside the frame, so nothing is cut off. Both are held by the same line of
 * arithmetic, because both are the same statement about overlap.</p>
 *
 * <p>{@code FIT} exists because a host's banner is rarely 3:1. Cover-only framing takes a horizontal
 * slice out of the middle of a square logo and offers no way to see the rest, which is the complaint
 * this mode answers. What surrounds a fitted image is <b>nothing</b>: the export keeps its alpha and
 * the page it lands on supplies the background, so the margins are the card's own colour in either
 * theme rather than a slab of white baked in at upload time.</p>
 */

/**
 * What we store, per kind.
 *
 * <p>Derived from where the image is actually rendered rather than picked as a round number:</p>
 * <ul>
 *   <li><b>Avatar</b> — displayed as a 96px circle, so 512 covers a 3x device with room to spare,
 *       and leaves headroom if it is ever shown larger. Square, not a circle: the circle is a CSS
 *       mask at render time, so the corners are kept and no detail is thrown away.</li>
 *   <li><b>Banner</b> — a 3:1 frame across a card at most 768px wide, so 1536x512 is exactly 2x.</li>
 * </ul>
 *
 * <p>Worth revisiting once there are real uploads to look at.</p>
 */
export const EXPORT_SIZE: Record<MediaKind, { width: number; height: number }> = {
  AVATAR: { width: 512, height: 512 },
  BANNER: { width: 1536, height: 512 },
};

/**
 * How the image meets the frame.
 *
 * <ul>
 *   <li><b>FILL</b> — every pixel of the FRAME is covered, and the image is cropped to achieve it.
 *       Right for a photo, where the subject matters and the edges do not.</li>
 *   <li><b>FIT</b> — every pixel of the IMAGE is kept, and the frame is left partly empty to achieve
 *       it. Right for a logo or a wordmark, where a crop destroys the thing itself.</li>
 * </ul>
 */
export type FrameMode = "FILL" | "FIT";

/**
 * Rotation, in right angles clockwise.
 *
 * <p>Quarter turns rather than free degrees, and that is a deliberate limit. Ninety degrees is the
 * fix for the one problem hosts actually have — a photo that arrived on its side — and it is exact:
 * the pixel grid survives a quarter turn untouched, where an arbitrary angle resamples every pixel
 * and leaves transparent wedges in the corners that {@code FIT} would then faithfully publish.</p>
 */
export type QuarterTurns = 0 | 1 | 2 | 3;

/**
 * The image's footprint after rotation, which is all the rest of the arithmetic needs to know.
 *
 * <p>An odd number of quarter turns swaps width for height, and every fitting decision here is made
 * from those two numbers. So rotation costs exactly one call at the boundary rather than a rotation
 * term threaded through {@link minScale}, {@link fitScale}, {@link clampOffset} and {@link destRect}
 * — each of which would then need its own proof.</p>
 */
export function orientedSize(image: Size, turns: QuarterTurns): Size {
  return turns % 2 === 0 ? image : { width: image.height, height: image.width };
}

/** Rotate clockwise, wrapping. The only way {@link QuarterTurns} is advanced. */
export function turnClockwise(turns: QuarterTurns): QuarterTurns {
  return ((turns + 1) % 4) as QuarterTurns;
}

/** Aspect ratio of the destination frame. The export matches it exactly, so nothing is re-cropped. */
export function frameAspect(kind: MediaKind): number {
  const { width, height } = EXPORT_SIZE[kind];
  return width / height;
}

/** How the image is placed under the frame: a uniform scale plus a centre offset, in frame pixels. */
export type Transform = { scale: number; dx: number; dy: number };

export type Size = { width: number; height: number };

/**
 * The smallest scale that still covers the frame.
 *
 * <p>The floor for zoom in {@code FILL}. Below it the image would be narrower or shorter than the
 * frame in one axis, and no offset could hide the gap.</p>
 */
export function minScale(image: Size, frame: Size): number {
  return Math.max(frame.width / image.width, frame.height / image.height);
}

/**
 * The largest scale at which the whole image still fits inside the frame.
 *
 * <p>The floor for zoom in {@code FIT}, and never above {@link minScale} — the two meet only when the
 * image already has the frame's exact ratio, which is the case where both modes describe the same
 * picture and the toggle stops mattering.</p>
 */
export function fitScale(image: Size, frame: Size): number {
  return Math.min(frame.width / image.width, frame.height / image.height);
}

/** The zoom floor for a mode. Zooming out past this is what the mode exists to permit or forbid. */
export function scaleFloor(mode: FrameMode, image: Size, frame: Size): number {
  return mode === "FIT" ? fitScale(image, frame) : minScale(image, frame);
}

/**
 * Pull an offset back inside the legal range.
 *
 * <p>{@code Math.abs} is doing real work here, and it is what lets one clamp serve both modes. When
 * the scaled image is LARGER than the frame the difference is overhang, and half of it is how far the
 * image may be dragged before a gap opens at the far edge. When it is SMALLER the difference is
 * slack, and half of it is how far it may be dragged before the image leaves the frame entirely. Same
 * number, opposite meanings, identical arithmetic.</p>
 *
 * <p>At the covering scale the difference is exactly zero, so the axis pins to centre — which is why
 * {@code FILL} behaves precisely as it did before {@code FIT} existed.</p>
 */
export function clampOffset(t: Transform, image: Size, frame: Size): Transform {
  const maxDx = Math.abs(image.width * t.scale - frame.width) / 2;
  const maxDy = Math.abs(image.height * t.scale - frame.height) / 2;
  return {
    scale: t.scale,
    dx: Math.min(maxDx, Math.max(-maxDx, t.dx)),
    dy: Math.min(maxDy, Math.max(-maxDy, t.dy)),
  };
}

/** Start centred at the mode's floor: the most of the image that mode is willing to show. */
export function initialTransform(image: Size, frame: Size, mode: FrameMode = "FILL"): Transform {
  return { scale: scaleFloor(mode, image, frame), dx: 0, dy: 0 };
}

/**
 * Where the image lands inside the frame, in frame pixels.
 *
 * <p>This is the whole placement, and the export multiplies it by a single ratio to reach the stored
 * size. Expressing it as a DESTINATION rather than a source rectangle is what lets one code path
 * serve both modes: a source rectangle cannot describe a fitted image, because the region it would
 * have to name extends past the image's own edges.</p>
 */
export function destRect(t: Transform, image: Size, frame: Size) {
  const width = image.width * t.scale;
  const height = image.height * t.scale;
  return {
    x: (frame.width - width) / 2 + t.dx,
    y: (frame.height - height) / 2 + t.dy,
    width,
    height,
  };
}

/**
 * A zoom ceiling, so a host cannot magnify a small image into mush.
 *
 * <p>Capped at whatever scale would render one source pixel per exported pixel — past that there is
 * no more detail to reveal, only interpolation. Never below the covering scale, or a small image
 * would have no legal range at all.</p>
 */
export function maxScale(image: Size, frame: Size, exportSize: Size): number {
  const oneToOne = exportSize.width / frame.width;
  return Math.max(minScale(image, frame), oneToOne);
}

/**
 * Which mode a transform currently expresses, for a toggle that has to show a state.
 *
 * <p>Read back from the geometry rather than tracked alongside it. A host who starts in {@code FIT}
 * and zooms until the frame is covered has, in every sense that matters, chosen {@code FILL}, and a
 * toggle still lit on {@code FIT} would be describing a picture other than the one in front of
 * them.</p>
 */
export function modeOf(t: Transform, image: Size, frame: Size): FrameMode {
  // A hair of tolerance, because the covering scale is reached through float multiplication.
  return t.scale >= minScale(image, frame) - 1e-6 ? "FILL" : "FIT";
}

/**
 * How much of the image survives if the frame is filled, as a fraction of its area.
 *
 * <p>Filling crops one axis and leaves the other whole, so the survivor is entirely a matter of how
 * far the two aspect ratios are apart. A 3:1 image in a 3:1 frame keeps all of itself; a square logo
 * in that frame keeps a third.</p>
 */
export function keptWhenFilled(image: Size, frame: Size): number {
  const a = image.width / image.height;
  const f = frame.width / frame.height;
  return Math.min(a, f) / Math.max(a, f);
}

/**
 * Below this, filling is throwing away more of the picture than it is showing, so {@code FIT} is the
 * better opening offer. Above it, a modest crop is what a host uploading a photo expects.
 */
const KEEP_TO_FILL = 0.6;

/**
 * Which mode to open on for a freshly picked image.
 *
 * <p>Not a fixed default, because the right one depends entirely on the picture. A wide photo filling
 * a 3:1 banner loses its top and bottom and looks intended; a square logo in the same frame loses
 * two thirds of itself and looks broken. Opening on the mode that keeps more of what the host chose
 * means the common cases need no decision at all, and the toggle is there for the rest.</p>
 *
 * <p>Biased toward {@code FILL}, which is what every upload did before {@code FIT} existed: an image
 * has to be genuinely far from the frame's shape before we start it pillarboxed.</p>
 */
export function suggestMode(image: Size, frame: Size): FrameMode {
  return keptWhenFilled(image, frame) < KEEP_TO_FILL ? "FIT" : "FILL";
}

/**
 * Carry a framing across a change in stage size.
 *
 * <p>The stage is measured from the viewport, so it changes when the window is resized or a phone is
 * rotated. Scale and offsets are both expressed in frame pixels, so both scale by the same ratio —
 * without this the picture would jump, or silently reframe itself, halfway through the host's
 * decision.</p>
 */
export function rescale(t: Transform, fromWidth: number, toWidth: number): Transform {
  const ratio = toWidth / fromWidth;
  return { scale: t.scale * ratio, dx: t.dx * ratio, dy: t.dy * ratio };
}
